'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  id: number;
  question: string;
  answer: string;
}

const faqs: FAQ[] = [
  {
    id: 1,
    question: 'Quelles sont les qualifications requises pour postuler?',
    answer: 'Les qualifications varient selon le poste. Pour les postes d\'agent de nettoyage, nous recherchons des personnes motivées avec ou sans expérience (formation fournie). Pour les postes de superviseur, nous exigeons au moins 3 ans d\'expérience en gestion d\'équipe. Tous les candidats doivent avoir un permis de travail valide en Suisse.'
  },
  {
    id: 2,
    question: 'Offrez-vous une formation aux nouveaux employés?',
    answer: 'Oui, absolument! Nous offrons une formation complète de 2 semaines pour tous les nouveaux employés, couvrant les techniques de nettoyage professionnelles, l\'utilisation des équipements, les protocoles de sécurité et les standards de qualité suisses. La formation continue est également disponible tout au long de votre carrière.'
  },
  {
    id: 3,
    question: 'Quels sont les horaires de travail?',
    answer: 'Nous offrons une grande flexibilité horaire. Les postes résidentiels sont généralement de 8h à 17h en semaine. Les postes commerciaux peuvent inclure des horaires de soirée (17h-22h) ou de week-end. Nous travaillons avec vous pour créer un planning qui correspond à vos besoins personnels.'
  },
  {
    id: 4,
    question: 'Combien de temps prend le processus de recrutement?',
    answer: 'Le processus complet prend généralement 2-3 semaines. Après réception de votre candidature, nous vous contactons dans les 5 jours ouvrables pour un premier entretien téléphonique. Si sélectionné, vous aurez un entretien en personne, suivi d\'une vérification des références. Les candidats retenus commencent la formation dans les 2 semaines suivant l\'offre.'
  },
  {
    id: 5,
    question: 'Y a-t-il des opportunités d\'avancement?',
    answer: 'Oui! Nous croyons fermement à la promotion interne. De nombreux superviseurs et chefs d\'équipe ont commencé comme agents de nettoyage. Nous offrons des programmes de développement professionnel, des formations en leadership et des opportunités de spécialisation dans différents domaines du nettoyage.'
  },
  {
    id: 6,
    question: 'Dois-je parler français et allemand?',
    answer: 'Pour la plupart des postes, la maîtrise du français est suffisante car c\'est la langue principale dans la région de Le Locle. Cependant, la connaissance de l\'allemand est un atout majeur et peut ouvrir plus d\'opportunités, particulièrement pour les postes de supervision et de contact client.'
  },
  {
    id: 7,
    question: 'Fournissez-vous les uniformes et équipements?',
    answer: 'Oui, nous fournissons tous les uniformes professionnels, équipements de protection individuelle (EPI), et produits de nettoyage nécessaires. Vous recevrez 3 uniformes complets lors de votre embauche, et nous les remplaçons régulièrement pour maintenir une apparence professionnelle.'
  },
  {
    id: 8,
    question: 'Puis-je travailler à temps partiel?',
    answer: 'Absolument! Nous offrons des postes à temps plein et à temps partiel. Les postes à temps partiel commencent généralement à 20 heures par semaine minimum. C\'est une excellente option pour les étudiants, les parents ou toute personne cherchant un complément de revenu avec des horaires flexibles.'
  }
];

interface FAQSectionProps {
  className?: string;
}

const FAQSection = ({ className = '' }: FAQSectionProps) => {
  const [openId, setOpenId] = useState<number | null>(null);

  const toggleFAQ = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section className={`py-16 lg:py-24 bg-surface ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
            Questions Fréquentes
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Trouvez les réponses aux questions les plus courantes sur le processus de recrutement et les conditions de travail
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq) => (
            <div
              key={faq.id}
              className="bg-card rounded-lg shadow-sm border border-border overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(faq.id)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-surface transition-colors duration-300"
              >
                <span className="font-heading font-semibold text-foreground pr-4">
                  {faq.question}
                </span>
                <Icon
                  name="ChevronDownIcon"
                  size={24}
                  variant="outline"
                  className={`flex-shrink-0 transition-transform duration-300 ${
                    openId === faq.id ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openId === faq.id ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-6 pb-4 text-muted-foreground leading-relaxed">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-muted-foreground mb-4">
            Vous avez d&apos;autres questions?
          </p>
          <a
            href="/contact"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-heading font-semibold hover:bg-primary/90 transition-colors duration-300"
          >
            <span>Contactez-nous</span>
            <Icon name="ArrowRightIcon" size={20} variant="outline" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;